import { toPng } from "html-to-image"
import { pdf } from "@react-pdf/renderer"

import { GanttPdfDocument } from "./GanttPdfDocument.tsx"
import {
  AVAILABLE_ROW_HEIGHT_PT,
  IMAGE_GAP_PT,
  USABLE_WIDTH_PT,
  type GanttPdfLayoutResult
} from "./gantt-pdf-constants.ts"

export interface GanttPdfResult {
  blob: Blob
  scaledDown: boolean
}

interface CapturedNode {
  dataUrl: string
  width: number
  height: number
}

async function captureNode(node: HTMLElement): Promise<CapturedNode> {
  const dataUrl = await toPng(node, { pixelRatio: 2, backgroundColor: "#ffffff" })
  return { dataUrl, width: node.scrollWidth, height: node.scrollHeight }
}

// Both images are scaled by one shared factor, and both get their final
// height from one shared natural height (never each one's own) — the two
// panels share an on-screen `rowHeight`, so this is what keeps milestone
// row N's label aligned with row N's bar. The factor is capped at 1 so a
// small project is never stretched blurry past its 2x-pixelRatio capture.
export function computeGanttPdfImageLayout(
  leftPanel: { width: number; height: number },
  chart: { width: number; height: number }
): GanttPdfLayoutResult {
  if (leftPanel.width <= 0 || leftPanel.height <= 0 || chart.width <= 0 || chart.height <= 0) {
    // A capture with no real size (briefly hidden, mid-transition) would
    // otherwise divide into NaN/Infinity and flow into <Image> as an
    // invalid style with no visible error.
    throw new Error("Cannot lay out an empty Gantt capture")
  }

  const scaleToFitWidth = (USABLE_WIDTH_PT - IMAGE_GAP_PT) / (leftPanel.width + chart.width)
  const naturalHeight = Math.max(leftPanel.height, chart.height)
  const scaleToFitHeight = AVAILABLE_ROW_HEIGHT_PT / naturalHeight
  const scale = Math.min(scaleToFitWidth, scaleToFitHeight, 1)

  return {
    layout: {
      leftPanelWidth: leftPanel.width * scale,
      leftPanelHeight: naturalHeight * scale,
      chartWidth: chart.width * scale,
      chartHeight: naturalHeight * scale
    },
    scaledDown: scale < 1
  }
}

export interface GanttPdfCapturedPage {
  leftPanel: { width: number; height: number }
  chart: { width: number; height: number }
}

// Every page is drawn at the one scale the tightest page needs, so a row is
// the same size on page 3 as on page 1; each page keeps its own height.
export function computeGanttPdfPagesLayout(pages: GanttPdfCapturedPage[]): {
  layouts: GanttPdfLayoutResult["layout"][]
  scaledDown: boolean
} {
  const scale = Math.min(
    ...pages.map(({ leftPanel, chart }) => {
      const { layout } = computeGanttPdfImageLayout(leftPanel, chart)
      return layout.chartWidth / chart.width
    })
  )
  return {
    layouts: pages.map(({ leftPanel, chart }) => {
      const height = Math.max(leftPanel.height, chart.height) * scale
      return {
        leftPanelWidth: leftPanel.width * scale,
        leftPanelHeight: height,
        chartWidth: chart.width * scale,
        chartHeight: height
      }
    }),
    scaledDown: scale < 1
  }
}

export async function buildGanttPdfBlob({
  pages,
  projectName,
  generatedAtLabel
}: {
  pages: { leftPanelEl: HTMLElement; chartEl: HTMLElement }[]
  projectName?: string
  // Pre-formatted by the caller's own `useDateFormatter` -- @react-pdf/renderer
  // builds its document via a separate renderer instance the app's Redux
  // store never reaches, so the account's date format can't be resolved here.
  generatedAtLabel: string
}): Promise<GanttPdfResult> {
  if (!pages.length) throw new Error("Cannot build a Gantt PDF with no pages")
  const captured: { leftPanel: CapturedNode; chart: CapturedNode }[] = []
  // One page at a time: each capture holds a full-resolution image in memory.
  for (const page of pages) {
    const [leftPanel, chart] = await Promise.all([
      captureNode(page.leftPanelEl),
      captureNode(page.chartEl)
    ])
    captured.push({ leftPanel, chart })
  }

  const { layouts, scaledDown } = computeGanttPdfPagesLayout(captured)

  const blob = await pdf(
    <GanttPdfDocument
      projectName={projectName}
      generatedAtLabel={generatedAtLabel}
      pages={captured.map(({ leftPanel, chart }, index) => ({
        leftPanelDataUrl: leftPanel.dataUrl,
        chartDataUrl: chart.dataUrl,
        layout: layouts[index]
      }))}
    />
  ).toBlob()

  return { blob, scaledDown }
}
